import axios from 'axios';
import type { Dispatch } from '@reduxjs/toolkit';

import { actions } from 'features/auth/slice';
import { findCountryWithAgencyConfigForUser } from 'features/auth/helpers';
import { getSalesOrderId } from 'features/salesOrder/services';
import type { ISaleOrderDetail } from 'features/salesOrder/types';
import type { CountryCodeEnum } from 'features/common/types';
import { extractErrorMessage } from 'features/quotation/helpers';

export function parseAgencyIdFromSearch(search: string): number | undefined {
	const raw = new URLSearchParams(search).get('agencyId');
	if (!raw) return undefined;
	const parsed = Number(raw);
	return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

export function userHasAgencyAccess(agencyId: number, userAgencyIds: number[]): boolean {
	return userAgencyIds.includes(agencyId);
}

function buildAgencyTryOrder(userAgencyIds: number[], preferredAgencyId?: number): number[] {
	const unique = [...new Set(userAgencyIds)];
	if (preferredAgencyId && unique.includes(preferredAgencyId)) {
		return [preferredAgencyId, ...unique.filter((id) => id !== preferredAgencyId)];
	}
	return unique;
}

export function ensureAgencyContextForSaleOrder(
	agencyId: number,
	currentCountry: CountryCodeEnum,
	userAgencyIds: number[],
	dispatch: Dispatch,
): { switched: boolean; countryName: string } | null {
	if (!userHasAgencyAccess(agencyId, userAgencyIds)) {
		return null;
	}

	const config = findCountryWithAgencyConfigForUser(agencyId);
	if (!config) return null;

	if (config.id === currentCountry) {
		return { switched: false, countryName: config.countryName };
	}

	dispatch(actions.setUpdateCountry(config.id));
	return { switched: true, countryName: config.countryName };
}

export async function fetchSaleOrderAcrossAgencies(
	saleOrderId: string,
	userAgencyIds: number[],
	preferredAgencyId?: number,
): Promise<{ data: ISaleOrderDetail; resolvedAgencyId: number }> {
	const agenciesToTry = buildAgencyTryOrder(userAgencyIds, preferredAgencyId);
	let lastError: unknown;

	for (const agencyId of agenciesToTry) {
		try {
			const { data } = await getSalesOrderId(saleOrderId, { agencyId });
			return { data, resolvedAgencyId: agencyId };
		} catch (error) {
			if (axios.isAxiosError(error) && error.response?.status === 404) {
				lastError = error;
				continue;
			}
			throw error;
		}
	}

	throw lastError ?? new Error('No se encontró la orden de venta');
}

export async function loadSaleOrderWithAgencyContext({
	saleOrderId,
	preferredAgencyId,
	userAgencyIds,
	currentCountry,
	dispatch,
}: {
	saleOrderId: string;
	preferredAgencyId?: number;
	userAgencyIds: number[];
	currentCountry: CountryCodeEnum;
	dispatch: Dispatch;
}): Promise<{
	data: ISaleOrderDetail;
	resolvedAgencyId: number;
	switched: boolean;
	countryName: string;
}> {
	if (preferredAgencyId && !userHasAgencyAccess(preferredAgencyId, userAgencyIds)) {
		throw new Error('No tenés permiso para acceder a la agencia de esta orden de venta.');
	}

	if (userAgencyIds.length === 0) {
		throw new Error('No tenés agencias asignadas para consultar esta orden de venta.');
	}

	const { data, resolvedAgencyId } = await fetchSaleOrderAcrossAgencies(saleOrderId, userAgencyIds, preferredAgencyId);

	const contextResult = ensureAgencyContextForSaleOrder(resolvedAgencyId, currentCountry, userAgencyIds, dispatch);

	if (!contextResult) {
		throw new Error('No tenés permiso para acceder a la agencia de esta orden de venta.');
	}

	return {
		data,
		resolvedAgencyId,
		switched: contextResult.switched,
		countryName: contextResult.countryName,
	};
}

export function getSaleOrderLoadErrorMessage(error: unknown): string {
	return extractErrorMessage(error, 'No se pudo cargar la orden de venta');
}
